import {
  Box,
  Checkbox,
  Chip,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, getModernScrollbar, getModernTable, layoutGap } from '../../../theme/theme';
import { OperationProgressPlaceholder } from '../../../shared/ui/progress';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { exportActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { mappingDensity } from '../../../shared/ui/mapping';
import { STATUS_CONFIG } from '../exportPcfConstants';
import type { ExportColumn } from '../hooks/useExportColumns';
import {
  ExportMobileCardHeight,
  ExportRowHeight,
  type SystemTableModel,
} from '../hooks/useSystemTableModel';
import type { PipingSystem } from '../types';
import { resolvePipingSystemStatus } from '../lib/exportSystemStatus';
import { formatStatusEvidence } from '../lib/systemTableUtils';
import type { ExportRowHighlightMap } from '../lib/rowHighlights';
import { getExportRowHighlightSx } from './rowHighlightStyles';
import ModelSelectButton from './ModelSelectButton';
import ExportFailureIndicator from './ExportFailureIndicator';
import { getSystemTableSelectionCellSx } from './systemTableSelectionCellStyles';
import {
  getExportFailuresForSystem,
  type ExportFailuresBySystemName,
} from '../lib/exportFailurePresentation';

interface SystemTableRowsProps {
  readonly model: SystemTableModel;
  readonly selected: Set<string>;
  readonly dynamicColumns: ExportColumn[];
  readonly loading?: boolean;
  readonly onStatusClick?: (system: PipingSystem) => void;
  readonly onSelectInModel?: (systems: PipingSystem[]) => void;
  readonly rowHighlightColors: ExportRowHighlightMap;
  readonly modelSelectionBusy: boolean;
  readonly selectionDisabled: boolean;
  readonly exportFailuresBySystemName: ExportFailuresBySystemName;
  readonly onOpenExportLog?: () => void;
}

function EmptySystems({
  loading,
  filtered,
  mobile = false,
}: {
  readonly loading?: boolean;
  readonly filtered: boolean;
  readonly mobile?: boolean;
}) {
  if (loading) {
    return (
      <OperationProgressPlaceholder
        compact
        model={{
          title: 'Loading piping systems',
          message: 'Requesting piping systems from Revit',
          detail: mobile
            ? 'The review list will render as soon as the cached export snapshot is available.'
            : 'The table will render as soon as the cached export snapshot is available.',
          status: 'running',
          percent: null,
          locked: true,
        }}
        sx={mobile ? { minHeight: 132, width: '100%' } : { minHeight: { xs: 160, sm: 190, md: 220 } }}
      />
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={mobile ? { fontSize: '0.78rem' } : undefined}>
      {filtered ? 'No systems match the current filters' : 'No piping systems found'}
    </Typography>
  );
}

function ExportStatusChip({
  system,
  onStatusClick,
}: {
  readonly system: PipingSystem;
  readonly onStatusClick?: (system: PipingSystem) => void;
}) {
  const theme = useTheme();
  const resolvedStatus = resolvePipingSystemStatus(system);
  const config = STATUS_CONFIG[resolvedStatus];
  const statusEvidence = formatStatusEvidence(system, resolvedStatus);
  const statusAriaLabel = onStatusClick
    ? `Open validation details for ${system.systemName}, ${statusEvidence}`
    : `${system.systemName} status, ${statusEvidence}`;
  let label = config.label;
  if (resolvedStatus === 'Warnings' && (system.warningCount || 0) > 0) {
    label = `${system.warningCount} warn`;
  }
  if (resolvedStatus === 'Errors' && (system.errorCount || 0) > 0) {
    label = `${system.errorCount} err`;
  }
  const statusColor = resolvedStatus === 'NotValidated'
    ? theme.palette.text.secondary
    : theme.palette[config.color].main;

  return (
    <ActionHelpTooltip help={exportActionTooltips.statusDrilldown}>
      <Chip
        label={label}
        icon={<Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'currentColor' }} />}
        size="small"
        clickable={Boolean(onStatusClick)}
        role={onStatusClick ? 'button' : undefined}
        tabIndex={onStatusClick ? 0 : undefined}
        aria-label={statusAriaLabel}
        onClick={onStatusClick ? (event) => {
          event.stopPropagation();
          onStatusClick(system);
        } : undefined}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (!onStatusClick || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
          }
          event.preventDefault();
          onStatusClick(system);
        }}
        sx={{
          height: 20,
          fontSize: '0.72rem',
          fontWeight: 600,
          backgroundColor: 'transparent',
          color: statusColor,
          cursor: onStatusClick ? 'pointer' : 'default',
          '& .MuiChip-icon': { ml: '6px', mr: '-2px' },
          '& .MuiChip-label': { px: '6px' },
          '&:hover': onStatusClick ? { backgroundColor: alpha(statusColor, 0.1) } : undefined,
        }}
      />
    </ActionHelpTooltip>
  );
}

function handleRowKeyDown(
  event: React.KeyboardEvent<HTMLElement>,
  systemName: string,
  handleToggle: (name: string) => void,
) {
  if (event.currentTarget !== event.target || (event.key !== 'Enter' && event.key !== ' ')) {
    return;
  }
  event.preventDefault();
  handleToggle(systemName);
}

export function SystemTableMobileRows(props: SystemTableRowsProps) {
  const theme = useTheme();
  const scrollbarStyles = getModernScrollbar(theme);
  const { model } = props;

  return (
    <Box
      ref={model.setRowsRegionRef}
      data-testid="export-pcf-system-table-scroll-region"
      data-tour-target="export-pcf-system-rows"
      role="region"
      aria-label="PCF export systems review list"
      aria-busy={Boolean(props.loading)}
      tabIndex={0}
      onScroll={model.handleTableScroll}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        borderRadius: `${borderRadius.none}px`,
        border: 'none',
        backgroundColor: alpha(theme.palette.background.default, 0.24),
        px: 1,
        py: 0.75,
        ...scrollbarStyles,
      }}
    >
      {model.filtered.length === 0 ? (
        <Box sx={{ minHeight: props.loading ? 160 : 132, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2 }}>
          <EmptySystems
            loading={props.loading}
            filtered={Object.keys(model.columnFilters).length > 0}
            mobile
          />
        </Box>
      ) : (
        <Box role="listbox" aria-label="PCF export systems" aria-multiselectable="true">
          {model.rowWindow.topPadding > 0 ? (
            <Box aria-hidden="true" sx={{ height: model.rowWindow.topPadding }} />
          ) : null}
          {model.rowWindow.visibleRows.map(({ system, values }, index) => {
            const isSelected = props.selected.has(system.systemName);
            const primaryColumn = props.dynamicColumns[0];
            const primaryValue = primaryColumn ? values[primaryColumn.id] : '';
            const failures = getExportFailuresForSystem(
              props.exportFailuresBySystemName,
              system.systemName,
            );

            return (
              <Box
                key={system.systemName}
                ref={index === 0 ? model.setFirstRowRef : undefined}
                data-testid={index === 0 ? 'export-pcf-tour-target-first-row' : undefined}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                aria-label={`${system.systemName}, ${isSelected ? 'selected' : 'not selected'}. Press Enter or Space to ${isSelected ? 'clear selection' : 'select system'}.`}
                onClick={() => model.handleToggle(system.systemName)}
                onKeyDown={(event) => handleRowKeyDown(event, system.systemName, model.handleToggle)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                  gridTemplateRows: 'auto auto',
                  alignItems: 'center',
                  columnGap: layoutGap.dense,
                  rowGap: 0.35,
                  height: ExportMobileCardHeight - 6,
                  mb: 0.65,
                  px: 0.85,
                  py: 0.65,
                  border: `1px solid ${alpha(isSelected ? theme.palette.primary.main : theme.palette.divider, isSelected ? 0.46 : 0.34)}`,
                  borderRadius: `${borderRadius.md}px`,
                  backgroundColor: isSelected
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.background.paper, 0.88),
                  ...getExportRowHighlightSx({ color: props.rowHighlightColors[system.systemName], selectedRow: isSelected, theme }),
                  cursor: 'pointer',
                  minWidth: 0,
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isSelected ? 0.11 : 0.045) },
                  '&:focus-visible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`, outlineOffset: -2 },
                }}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={props.selectionDisabled}
                  size="small"
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => model.handleToggle(system.systemName)}
                  inputProps={{
                    'aria-label': isSelected
                      ? `Clear ${system.systemName} selection`
                      : `Select ${system.systemName}`,
                  }}
                  sx={{ gridRow: '1 / 3', p: 0.25 }}
                />
                <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {system.systemName}
                  </Typography>
                  <ExportFailureIndicator
                    systemName={system.systemName}
                    failures={failures}
                    onOpenExportLog={props.onOpenExportLog}
                  />
                </Box>
                <Box sx={{ justifySelf: 'end', display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                  <ExportStatusChip system={system} onStatusClick={props.onStatusClick} />
                  <ModelSelectButton
                    system={system}
                    compact
                    modelSelectionBusy={props.modelSelectionBusy}
                    onSelectInModel={props.onSelectInModel}
                  />
                </Box>
                <Box sx={{ gridColumn: '2 / 4', display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.66rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                    {system.elementCount ?? 0} elem
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.66rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                    {system.sessionExportCount ?? 0} exp
                  </Typography>
                  {primaryColumn ? (
                    <Typography variant="caption" title={`${primaryColumn.label}: ${primaryValue}`} sx={{ color: 'text.secondary', fontSize: '0.66rem', lineHeight: 1.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {primaryColumn.label}: {primaryValue || '-'}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
          {model.rowWindow.bottomPadding > 0 ? (
            <Box aria-hidden="true" sx={{ height: model.rowWindow.bottomPadding }} />
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export function SystemTableDesktopRows(props: SystemTableRowsProps) {
  const theme = useTheme();
  const tableStyles = getModernTable(theme);
  const totalColumns = 6 + props.dynamicColumns.length;
  const { model } = props;

  return (
    <TableBody>
      {model.filtered.length === 0 ? (
        <TableRow>
          <TableCell colSpan={totalColumns} sx={{ textAlign: 'center', py: 6 }}>
            <EmptySystems
              loading={props.loading}
              filtered={Object.keys(model.columnFilters).length > 0}
            />
          </TableCell>
        </TableRow>
      ) : (
        <>
          {model.rowWindow.topPadding > 0 ? (
            <TableRow aria-hidden="true">
              <TableCell colSpan={totalColumns} sx={{ border: 0, height: model.rowWindow.topPadding, p: 0 }} />
            </TableRow>
          ) : null}
          {model.rowWindow.visibleRows.map(({ system, values }, index) => {
            const isSelected = props.selected.has(system.systemName);
            const failures = getExportFailuresForSystem(
              props.exportFailuresBySystemName,
              system.systemName,
            );
            return (
              <TableRow
                key={system.systemName}
                ref={index === 0 ? model.setFirstRowRef : undefined}
                data-testid={index === 0 ? 'export-pcf-tour-target-first-row' : undefined}
                hover
                selected={isSelected}
                tabIndex={0}
                aria-selected={isSelected}
                aria-label={`${system.systemName}, ${isSelected ? 'selected' : 'not selected'}. Press Enter or Space to ${isSelected ? 'clear selection' : 'select system'}.`}
                onClick={() => model.handleToggle(system.systemName)}
                onKeyDown={(event) => handleRowKeyDown(event, system.systemName, model.handleToggle)}
                sx={{
                  cursor: 'pointer',
                  height: ExportRowHeight,
                  '&:focus-visible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`, outlineOffset: -2 },
                  ...(isSelected ? tableStyles.selectedRow : {}),
                  ...getExportRowHighlightSx({ color: props.rowHighlightColors[system.systemName], selectedRow: isSelected, theme }),
                }}
              >
                <TableCell sx={getSystemTableSelectionCellSx(model.fixedColumnWidth.selection)}>
                  <Checkbox
                    checked={isSelected}
                    disabled={props.selectionDisabled}
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => model.handleToggle(system.systemName)}
                    inputProps={{
                      'aria-label': isSelected
                        ? `Clear ${system.systemName} selection`
                        : `Select ${system.systemName}`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: mappingDensity.tableCellFontSize, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {system.systemName}
                    </Typography>
                    <ExportFailureIndicator
                      systemName={system.systemName}
                      failures={failures}
                      onOpenExportLog={props.onOpenExportLog}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontSize: mappingDensity.tableCellFontSize, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {system.elementCount}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontSize: mappingDensity.tableCellFontSize, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {system.sessionExportCount ?? 0}
                  </Typography>
                </TableCell>
                {props.dynamicColumns.map((column) => (
                  <TableCell key={column.id}>
                    <Typography variant="body2" sx={{ fontSize: mappingDensity.tableCellFontSize, fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {values[column.id] ?? ''}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <ExportStatusChip system={system} onStatusClick={props.onStatusClick} />
                </TableCell>
                <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                  <ModelSelectButton
                    system={system}
                    modelSelectionBusy={props.modelSelectionBusy}
                    onSelectInModel={props.onSelectInModel}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {model.rowWindow.bottomPadding > 0 ? (
            <TableRow aria-hidden="true">
              <TableCell colSpan={totalColumns} sx={{ border: 0, height: model.rowWindow.bottomPadding, p: 0 }} />
            </TableRow>
          ) : null}
        </>
      )}
    </TableBody>
  );
}
